import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { getPreferences } from '../api'
import { getClientId } from '../session'
import UpgradePlans from '../components/UpgradePlans'
import { currentPaidTier } from '../plans'
import Spinner from '../components/Spinner'
import { ErrorAlert } from '../components/Alert'
import { useDocumentTitle } from '../components/PublicLayout'

/**
 * The plan grid, with checkout, at a URL of its own (#210 decision 8).
 *
 * Every "upgrade" link lands here: the digest's Starter teaser, Preferences,
 * corpus search, and the "subscription ended" view. Before this the grid existed
 * only inside the dashboard's collapsed banner, which that view does not render,
 * so no link could reach it, and they all went to /pricing, whose only button
 * starts a NEW trial.
 *
 * Preferences, not a product route: GET /api/preferences answers for an ended
 * plan (it is on get_current_client, not require_active_plan), which is exactly
 * the customer this page must still serve.
 */
export default function Plans() {
  useDocumentTitle('Plans')
  const navigate = useNavigate()
  const [prefs, setPrefs] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    getPreferences()
      .then(setPrefs)
      .catch(err => {
        // The API clears the cached session on a 401; bounce to sign-in.
        if (!getClientId()) { navigate('/signin'); return }
        setError(err.message)
      })
      .finally(() => setLoading(false))
  }, [])

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]"><Spinner /></div>
  )

  // Same rule as the dashboard banner: beta accounts are on the house and an
  // active Consultant plan is the top tier, so neither is offered a checkout.
  const isBeta = prefs?.plan_status === 'beta'
  const topTier = prefs?.plan_status === 'active' && prefs?.plan_tier === 'consultant'

  return (
    <div className="max-w-5xl mx-auto px-4 py-10 space-y-6">
      <div>
        <Link to="/dashboard" className="text-sm text-gray-500 hover:text-gray-700">
          ← Back to Dashboard
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 mt-2">Plans</h1>
        <p className="text-gray-500 text-sm mt-1">
          Monthly plans. Cancel anytime from Account settings.
        </p>
      </div>
      <ErrorAlert message={error} />
      {isBeta ? (
        <p className="rounded-xl border border-brand-200 bg-brand-50/60 px-5 py-4 text-sm text-gray-700">
          Your beta account includes every Pro feature at no cost, so there is
          nothing to choose here.
        </p>
      ) : topTier ? (
        <p className="rounded-xl border border-brand-200 bg-brand-50/60 px-5 py-4 text-sm text-gray-700">
          You are on the Consultant plan, which includes everything. Billing
          changes are in <Link to="/account" className="text-brand-500 hover:underline">Account settings</Link>.
        </p>
      ) : (
        <UpgradePlans currentTier={currentPaidTier(prefs)} />
      )}
    </div>
  )
}
